import React, { Component } from 'react';
import {
  Button,
  ButtonGroup,
  Badge,
  SplitButton,
  Dropdown,
  Tooltip,
  OverlayTrigger,
  Form,
  Table,
  Row,
  Col,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt,
  faEraser,
} from '@whitebox-learning/fontawesome-pro-light-svg-icons';
import { Loading } from '../Loading/Loading';
import ContenderTable from './ContenderTable';
import {
  SIMULATION_GROUPING_DISTRICT,
  SIMULATION_GROUPING_GROUP,
  SIMULATION_GROUPING_CLASSROOM,
  SIMULATION_GROUPING_MINE,
  SIMULATION_GROUPING_TOP,
  SIMULATION_FILTER_ALL,
  SIMULATION_FILTER_STAGED,
  SIMULATION_FILTER_TESTED,
  SIMULATION_FILTER_INVALID,
} from '../../constants/simulation';

import { DragsterIcon, CancelIcon } from '../Common/Icons';

import './Simulation.sass';

const Setup = ({
  loading,
  isGameMode,
  designList,
  designGrouping,
  designFilter,
  setDesignFilter,
  setSimulationSlot,
  setDesignGrouping,
  setTopCount,
  loadContenders,
  topCount,
  slot1,
  slot2,
  startSimulation,
  hasDistrict,
  studentInfo,
  competition,
  appCode,
}) => {
  const [nameSearchText, setNameSearchText] = React.useState('');
  const [contenderField, setContenderField] = React.useState(slot1?.id ? 1 : 2); // Default value is NOT an error - it MUST be this way because useEffect() call will flip it immediately on component mount
  const [skipUnity, setSkipUnity] = React.useState(false);

  const skipUnityApps = ["dragster", "glider", "water-rocket", "mousetrap-car", "structures"];
  const allowSkipUnity = skipUnityApps.includes(appCode); 

  React.useEffect(() => {
    if (contenderField === 1 && !slot2?.id) {
      setContenderField(2);
    }
    if (contenderField === 2 && !slot1?.id) {
      setContenderField(1);
    }
  }, [slot1, slot2]);

  const setSlot = (id, name) => {
    setSimulationSlot(contenderField, id, name);
  };

  const handleSetDesignGrouping = async (designGrouping) => {
    await setDesignGrouping(designGrouping);
    if (!isGameMode) {
      loadContenders();
    }
  };

  const filterRegex = new RegExp(nameSearchText, 'gi');
  let filteredDesignList;

  if (isGameMode && designGrouping === SIMULATION_GROUPING_MINE) {
    filteredDesignList = designList.filter((design) => {
      return design.studentId == studentInfo.studentIdNumber;
    });
  } else if (designGrouping === SIMULATION_GROUPING_TOP) {
    filteredDesignList = designList.filter((design) => {
      return (
        design.status === SIMULATION_FILTER_TESTED && design.rank <= topCount
      );
    });
  } else {
    filteredDesignList = designList
      .filter((design) => {
        if (nameSearchText && !design.name.match(filterRegex)) {
          return false;
        }
        return true;
      })
      .filter((design) => {
        return (
          designFilter === SIMULATION_FILTER_ALL ||
          designFilter === design.status
        );
      });
  }

  const counts = designList.reduce(
    (acc, cur) => {
      acc[cur.status]++;
      return acc;
    },
    {
      [SIMULATION_FILTER_STAGED]: 0,
      [SIMULATION_FILTER_INVALID]: 0,
      [SIMULATION_FILTER_TESTED]: 0,
    }
  );

  return (
    <div className="simulation-setup d-flex flex-column">
      {!!loading && <Loading />}

      {competition && (
        <div class="text-center">
          <img src={competition.logo_image} />
        </div>
      )}
      <h1 className="title border-bottom mb-3">
        Competition{competition && ': ' + competition.name}
      </h1>

      <div
        className="m-3 mt-0 text-center d-flex justify-content-between"
        id="simulation-setup-contenders"
      >
        <div className="d-flex justify-content-around align-items-center flex-grow-1">
          <Button
            variant="invisible"
            onClick={() => setContenderField(1)}
            active={contenderField === 1}
            aria-label={`Select Contender 1 slot${slot1?.name ? `: Currently ${slot1.name}` : ''}`}
            aria-pressed={contenderField === 1}
          >
            {slot1?.name ? (
              <strong>
                {/* <DragsterIcon size='2x' className="red" /><br /> */}
                {slot1.name}
              </strong>
            ) : (
              <span className="text-muted">
                {contenderField === 1 ? 'Choose from table' : '(not selected)'}
              </span>
            )}
            <br />
            <i>Contender 1</i>
          </Button>

          <div aria-hidden="true">
            <i>versus</i>
          </div>

          <Button
            variant="invisible"
            onClick={() => setContenderField(2)}
            active={contenderField === 2}
            aria-label={`Select Contender 2 slot${slot2?.name ? `: Currently ${slot2.name}` : ''}`}
            aria-pressed={contenderField === 2}
          >
            {slot2?.name ? (
              <strong>
                {/* <DragsterIcon size='2x' className="blue" /><br /> */}
                {slot2.name}
              </strong>
            ) : (
              <span className="text-muted">
                {contenderField === 2 ? 'Choose from table' : '(not selected)'}
              </span>
            )}
            <br />
            <i>Contender 2</i>
          </Button>
        </div>

        <div className="d-flex flex-column">
          <Button
            variant={slot1?.id && slot2?.id ? 'wb' : 'secondary'}
            disabled={!slot1?.id || !slot2?.id}
            onClick={() => startSimulation(skipUnity)}
            className="fw-bold"
          >
            Start Competition!
          </Button>
          {allowSkipUnity
           && <Form.Group>
                <Form.Check type="checkbox"
                            id="skip-unity-checkbox"
                            checked={skipUnity}
                            onChange={(e) => setSkipUnity(e.target.checked)}
                            label="Skip Animation"
                />
              </Form.Group>}
        </div>
     </div>

      <div className="p-3 pt-0 d-flex flex-column flex-grow-1">
        <div
          className="rounded bg-white p-3 d-flex flex-column flex-grow-1"
          id="simulation-contenders-list"
        >
          <p>
            <strong>Filter and Choose Contenders</strong>
          </p>

          <fieldset className="d-flex justify-content-between">
            <legend className="h6">Filter by:</legend>
            <ButtonGroup size="sm" role="group" aria-label="Filter designs by status">
              <Button
                variant="light"
                onClick={() => setDesignFilter(SIMULATION_FILTER_ALL)}
                active={designFilter === SIMULATION_FILTER_ALL}
                className="pe-2"
              >
                <Badge className="me-2">
                  {designList.length}
                </Badge>
                All
              </Button>
              <Button
                variant="light"
                onClick={() => setDesignFilter(SIMULATION_FILTER_STAGED)}
                active={designFilter === SIMULATION_FILTER_STAGED}
                className="pe-2"
              >
                <Badge className="me-2">
                  {counts[SIMULATION_FILTER_STAGED]}
                </Badge>
                Staged
              </Button>
              <Button
                variant="light"
                onClick={() => setDesignFilter(SIMULATION_FILTER_TESTED)}
                active={designFilter === SIMULATION_FILTER_TESTED}
                className="pe-2"
              >
                <Badge className="me-2">
                  {counts[SIMULATION_FILTER_TESTED]}
                </Badge>
                Qualifiers
              </Button>
              <Button
                variant="light"
                onClick={() => setDesignFilter(SIMULATION_FILTER_INVALID)}
                active={designFilter === SIMULATION_FILTER_INVALID}
                className="pe-2"
              >
                <Badge className="me-2">
                  {counts[SIMULATION_FILTER_INVALID]}
                </Badge>
                Out of Spec
              </Button>
            </ButtonGroup>
          </fieldset>
          <fieldset className="d-flex justify-content-between mt-3">
            <legend className="h6">Display:</legend>
            <ButtonGroup size="sm" role="group" aria-label="Display designs by scope">
              {isGameMode && (
                <Button
                  variant={
                    designGrouping === SIMULATION_GROUPING_GROUP
                      ? 'primary'
                      : 'light'
                  }
                  active={designGrouping === SIMULATION_GROUPING_GROUP}
                  onClick={() =>
                    handleSetDesignGrouping(SIMULATION_GROUPING_GROUP)
                  }
                >
                  All Designs
                </Button>
              )}
              {/* Geographic scope filters (only when not in game mode) */}
              {!isGameMode && (
                <>
                  {hasDistrict && (
                    <Button
                      variant={
                        designGrouping === SIMULATION_GROUPING_DISTRICT
                          ? 'primary'
                          : 'light'
                      }
                      active={designGrouping === SIMULATION_GROUPING_DISTRICT}
                      onClick={() =>
                        handleSetDesignGrouping(SIMULATION_GROUPING_DISTRICT)
                      }
                    >
                      My District
                    </Button>
                  )}
                  <Button
                    variant={
                      designGrouping === SIMULATION_GROUPING_GROUP
                        ? 'primary'
                        : 'light'
                    }
                    active={designGrouping === SIMULATION_GROUPING_GROUP}
                    onClick={() =>
                      handleSetDesignGrouping(SIMULATION_GROUPING_GROUP)
                    }
                  >
                    My Group
                  </Button>
                  <Button
                    variant={
                      designGrouping === SIMULATION_GROUPING_CLASSROOM
                        ? 'primary'
                        : 'light'
                    }
                    active={designGrouping === SIMULATION_GROUPING_CLASSROOM}
                    onClick={() =>
                      handleSetDesignGrouping(SIMULATION_GROUPING_CLASSROOM)
                    }
                  >
                    My Classroom
                  </Button>
                </>
              )}
              <Button
                variant={
                  designGrouping === SIMULATION_GROUPING_MINE
                    ? 'primary'
                    : 'light'
                }
                active={designGrouping === SIMULATION_GROUPING_MINE}
                onClick={() =>
                  handleSetDesignGrouping(SIMULATION_GROUPING_MINE)
                }
              >
                My Designs
              </Button>

              <span className="mx-1">or</span>
              <SplitButton
                id="toptenselector"
                variant={
                  designGrouping === SIMULATION_GROUPING_TOP
                    ? 'primary'
                    : 'light'
                }
                size="sm"
                title={'Top ' + topCount}
                toggleLabel="Toggle Top 10 dropdown"
                onClick={() => handleSetDesignGrouping(SIMULATION_GROUPING_TOP)}
                onSelect={setTopCount}
                className="ms-1"
              >
                <Dropdown.Item eventKey={10} id="top10">
                  Top 10
                </Dropdown.Item>
                <Dropdown.Item eventKey={25} id="top25">
                  Top 25
                </Dropdown.Item>
                <Dropdown.Item eventKey={50} id="top50">
                  Top 50
                </Dropdown.Item>
                <Dropdown.Item eventKey={100} id="top100">
                  Top 100
                </Dropdown.Item>
              </SplitButton>
            </ButtonGroup>
          </fieldset>

          <Row className="my-3">
            <Col>
              <Form.Label htmlFor="design-name-search">Design name contains:</Form.Label>
            </Col>
            <Col>
              <Form.Control
                id="design-name-search"
                type="text"
                size="sm"
                placeholder="Search design names"
                value={nameSearchText}
                onChange={(e) => setNameSearchText(e.target.value)}
              />
            </Col>
          </Row>

          <div className="flex-grow-1 overflow-y-auto flex-basis-overflow">
            <Table striped hover className="fixed-table-header" aria-label="Available designs for competition">
              <thead>
                <tr>
                  {designGrouping === SIMULATION_GROUPING_TOP && <th scope="col">Rank</th>}
                  <th scope="col">Design name</th>
                  <th scope="col">Engineer</th>
                  <th scope="col">{competition ? 'Team' : 'School'}</th>
                  <th scope="col">Location</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDesignList.map((d) => (
                  <tr key={d.id} valign="middle">
                    {designGrouping === SIMULATION_GROUPING_TOP && (
                      <td>{d.rank}</td>
                    )}
                    <td>{d.name}</td>
                    <td>{d.studentAlias || 'Anonymous'}</td>
                    <td>
                      {competition ? d.classroomName : d.organizationName}
                    </td>
                    <td>
                      {d.city}, {d.state}
                    </td>
                    <td valign="middle">
                      {slot1?.id === d.id && (
                        <span className="me-2">
                          <strong>Contender 1</strong>
                          <a
                            href="#"
                            onClick={() => setSimulationSlot(1)}
                            className="ms-1 fs-6"
                            aria-label={`Clear ${d.name} from Contender 1`}
                          >
                            [clear]
                          </a>
                        </span>
                      )}
                      {slot2?.id === d.id && (
                        <span>
                          <strong>Contender 2</strong>
                          <a
                            href="#"
                            onClick={() => setSimulationSlot(2)}
                            className="ms-1 fs-6"
                            aria-label={`Clear ${d.name} from Contender 2`}
                          >
                            [clear]
                          </a>
                        </span>
                      )}
                      {(contenderField === 1
                        ? slot1?.id !== d.id
                        : slot2?.id !== d.id) && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setSlot(d.id, d.name)}
                          aria-label={`Choose ${d.name} as Contender ${contenderField}`}
                        >
                          Choose (Contender {contenderField})
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {!filteredDesignList.length && (
              <div className="text-center text-muted m-3 mx-auto">
                No designs found. Try changing your search filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup;
